/**
 * Atlas PM · 系统设置 · 任务模板 · 删除二次确认弹窗（P8.2）。
 *
 * ⚠️ 后端为**物理删除**（无软删）。越权时后端返回
 *    403 TEMPLATE_DELETE_FORBIDDEN，message 原样展示（不吞错）。
 */

import { useEffect } from 'react';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { ApiError } from '../../../api/client';
import { type TaskTemplateItem } from './api';
import { useDeleteTaskTemplate } from './useTaskTemplates';

export interface DeleteTaskTemplateDialogProps {
  template: TaskTemplateItem | null;
  open: boolean;
  onClose(): void;
  onDeleted?(id: string): void;
}

export default function DeleteTaskTemplateDialog({
  template,
  open,
  onClose,
  onDeleted,
}: DeleteTaskTemplateDialogProps) {
  const remove = useDeleteTaskTemplate();

  // 每次打开清掉上一次的失败信息，避免跨条目串错
  useEffect(() => {
    if (open) remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = () => {
    if (!template) return;
    remove.mutate(template.id, {
      onSuccess: () => {
        onDeleted?.(template.id);
        onClose();
      },
    });
  };

  const errorMessage =
    remove.error instanceof ApiError
      ? remove.error.message
      : remove.error instanceof Error
        ? remove.error.message
        : null;

  return (
    <Modal
      open={open}
      size="sm"
      danger
      title={`删除任务模板 · ${template?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            取消
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            disabled={remove.isPending}
            data-testid="tpl-delete-confirm"
          >
            {remove.isPending ? '删除中…' : '确认删除'}
          </Button>
        </>
      }
    >
      <div className="pm-delete-meta">
        范围：<b>{template?.scope === 'PUBLIC' ? '公共' : '个人'}</b> · 创建人：
        <b>{template?.createdByName ?? '—'}</b>
      </div>

      <div className="pm-delete-explain">
        删除为物理删除，<b>不可恢复</b>。
        <br />
        删除后该模板不再出现在「从模板新建」列表中；已按此模板创建的任务不受影响。
      </div>

      {errorMessage && (
        <div className="pm-modal__err" data-testid="tpl-delete-error">
          ⚠ {errorMessage}
        </div>
      )}
    </Modal>
  );
}
